/**
 * Zod schemas for Contact validation
 */

import { z } from 'zod';
import type { Contact } from '@/modules/contact/types';
import { PhoneSchema } from '@/modules/phone';
import { generateUUID } from '@/util/uuid';

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const URN_UUID_RE =
  /^urn:uuid:([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i;

/**
 * Extract a UUID from a string that is either a bare UUID or a urn:uuid: prefixed UUID.
 * Returns the UUID portion, or null if the string doesn't match.
 */
export function extractUUID(value: string): string | null {
  if (UUID_RE.test(value)) return value;
  const match = value.match(URN_UUID_RE);
  return match ? match[1] : null;
}

export const ContactSchema = z
  .object({
    uid: z.string().optional(),
    prefix: z.string(),
    firstName: z.string(),
    middleName: z.string(),
    lastName: z.string(),
    suffix: z.string(),
    nickname: z.string(),
    companyName: z.string().optional(),
    department: z.string().optional(),
    subDepartment: z.string().optional(),
    tels: z
      .array(PhoneSchema)
      .min(1, 'Contact must have at least one phone number'),
    listingCategories: z.array(z.string()),
    fnPassthru: z.string().optional(),
    snowballId: z.string().optional(),
    lastSnowballRefreshDate: z.string().optional(),
    vCardParts: z
      .object({
        header: z.array(z.string()),
        footer: z.array(z.string()),
        unparsedLines: z.array(z.string()),
      })
      .optional(),
  })
  .transform(data => {
    let localId: string;
    let uid = data.uid;

    if (uid) {
      const extracted = extractUUID(uid);
      localId = extracted ?? generateUUID();
    } else {
      localId = generateUUID();
      uid = 'urn:uuid:' + localId;
    }

    return {
      ...data,
      localId,
      uid,
    };
  });

/**
 * Validate a Contact object
 */
export function validateContact(data: unknown): Contact | null {
  const result = ContactSchema.safeParse(data);
  if (!result.success) {
    // eslint-disable-next-line no-console
    console.error('Contact validation failed:', result.error.errors);
    return null;
  }
  return result.data as Contact;
}
